import { TeButton } from '@topcon/tuicl-react';
import { PlaceholderContainer, useTranslation } from '@topcon/topcon-utils';
import { IconName } from '@topcon/tuicl';

type Kind = 'noItems' | 'noSearch' | 'error';
type Resource = 'users' | 'companies' | 'customers' | 'dealers' | 'divisions' | 'apps';
type Subset = 'requests' | 'invites';

const ICON_BY_RESOURCE: Record<Resource, IconName> = {
  users: 'groups',
  customers: 'handshake',
  dealers: 'handshake',
  companies: 'business',
  divisions: 'business',
  apps: 'apps',
};

/** `error` is resource-independent: one shared key pair for every list. */
function i18nBase(kind: Kind, resource: Resource, subset?: Subset) {
  if (kind === 'error') {
    return 'emptyState.error';
  }
  return subset ? `emptyState.${resource}.${subset}.${kind}` : `emptyState.${resource}.${kind}`;
}

function useDefaults(kind: Kind, resource: Resource, subset?: Subset) {
  const { t } = useTranslation('common');

  const base = i18nBase(kind, resource, subset);

  if (kind === 'error') {
    return {
      icon: 'error' as IconName,
      title: t(`${base}.title`),
      subtitle: t(`${base}.subtitle`, ''),
      cta: undefined,
    };
  }

  if (kind === 'noSearch') {
    return {
      icon: 'Search' as IconName,
      title: t(`${base}.title`),
      subtitle: t(`${base}.subtitle`, ''),
      cta: undefined,
    };
  }

  return {
    icon: ICON_BY_RESOURCE[resource],
    title: t(`${base}.title`),
    subtitle: t(`${base}.subtitle`, ''),
    cta: t(`${base}.cta`, ''),
  };
}

type EmptyStateProps = {
  kind: Kind;
  resource: Resource;
  subset?: Subset;
  iconName?: IconName;
  title?: string;
  subtitle?: string;
  ctaLabel?: string;
  onCtaClick?: () => void;
  compact?: boolean;
  align?: 'center' | 'start';
  hasLoader?: boolean;
};

export default function EmptyState({
  kind,
  resource,
  subset,
  title,
  subtitle,
  iconName,
  ctaLabel,
  onCtaClick,
  hasLoader,
}: EmptyStateProps) {
  const { t } = useTranslation('common');
  const d = useDefaults(kind, resource, subset);

  const translation = i18nBase(kind, resource, subset);

  const resolvedTitle =
    title ??
    t(`${translation}.title`, {
      defaultValue: d.title,
    });

  const resolvedSubtitle =
    subtitle ??
    (t(`${translation}.subtitle`, {
      defaultValue: d.subtitle ?? '',
    }) ||
      undefined);

  const defaultCTA =
    !subset && kind === 'noItems'
      ? t(`${translation}.cta`, { defaultValue: d.cta ?? '' }) || undefined
      : undefined;

  const resolvedIcon = iconName ?? d.icon;
  const showCTA =
    !subset && kind === 'noItems' && (ctaLabel ?? defaultCTA) && typeof onCtaClick === 'function';

  if (hasLoader) {
    return <PlaceholderContainer hasLoader text={t('serviceAgreement.loading')} />;
  }

  return (
    <PlaceholderContainer text={resolvedTitle} secondaryText={resolvedSubtitle} icon={resolvedIcon}>
      {showCTA ? <TeButton text={(ctaLabel ?? defaultCTA)!} size="s" teclick={onCtaClick} /> : null}
    </PlaceholderContainer>
  );
}
